/**
 * Departments — the reporting tree inside a company, used for cost
 * attribution and approval routing.
 *
 * Arabic and English (C7): every word from the catalogs.
 */
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { OrgListPage } from "./OrgListPage";
import type { CompanyRow, DepartmentRow } from "./types";

export function DepartmentsPage() {
  const { t } = useTranslation();
  const companies = useApiList<CompanyRow>(["org", "companies"], "/companies");
  const departments = useApiList<DepartmentRow>(
    ["org", "departments"],
    "/departments",
  );

  return (
    <OrgListPage<DepartmentRow>
      title={t("org.departments.title")}
      subtitle={t("org.departments.subtitle")}
      breadcrumb={[t("org.section"), t("org.departments.title")]}
      path="/departments"
      queryKey="departments"
      createLabel={t("org.departments.new")}
      emptyTitle={t("org.departments.emptyTitle")}
      emptyMessage={t("org.departments.emptyMessage")}
      parent={{
        label: t("fields.company"),
        field: "companyId",
        options: (companies.data?.items ?? []).map((c) => ({
          id: c.id,
          label: `${c.name} (${c.code})`,
        })),
        isLoading: companies.isLoading,
        missingMessage: t("org.departments.missingParent"),
      }}
      columns={[
        { key: "name", label: t("fields.name") },
        {
          key: "code",
          label: t("fields.code"),
          render: (r) => <span className="atlas-mono">{r.code}</span>,
        },
        {
          key: "company_id",
          label: t("fields.company"),
          render: (r) =>
            companies.data?.items.find((c) => c.id === r.company_id)?.name ??
            "—",
        },
        {
          key: "parent_department_id",
          label: t("org.departments.reportsTo"),
          render: (r) =>
            r.parent_department_id
              ? (departments.data?.items.find(
                  (d) => d.id === r.parent_department_id,
                )?.name ?? "—")
              : t("org.departments.topLevel"),
        },
      ]}
    />
  );
}
